// Secure profile fetcher
// Updated: 2026-09-03
import type { SecureProfileResponse } from '@mrb91002/shared-types'

import { DEFAULT_LISTING_BUCKET, type ListingBucket } from '../listingBuckets'
import { secureFetch, SECURE_API_BASE } from '../secure/helpers'

/**
 * PERSONAL owner profile payload (lambda-secure `GET /api/secure/profile/{screenName}`).
 *
 * Identity-VARYING, which is why the query layer keys it `['me', 'profile', screenName]` and gates it
 * on `!!idToken`: `get-secure-profile.ts` resolves the caller with `getIdentityId(event)` and answers
 * 401 without it, passes `includePrivatePatterns: true`, and attaches the owner-only
 * `unpublishedDrafts` / `permissions` plus `private_pattern_count` / `archived_pattern_count` /
 * `*_collection_count`.
 *
 * ## Two bucket parameters, one per listing half (Requirement 5.3; tasks 29 and 49)
 *
 * This payload carries TWO owner listings, and each is ONE Listing_Bucket:
 *
 *   - `?bucket=` selects the PATTERNS half (task 18 gave it the key condition, task 29 the parameter).
 *   - `?collectionsBucket=` selects the COLLECTIONS half (task 49; BL-329). Before it, the owner branch
 *     of `shared-profile-fetcher.ts` composed `ACTIVE_LISTING_BUCKETS` — public + unlisted + private in
 *     one array — which is exactly the combined listing spanning buckets Requirement 5.3 forbids. Its
 *     own comment said task 29 would replace it and task 29 could not, because there was only ever a
 *     parameter for the patterns half.
 *
 * They are two parameters rather than one because the two halves are independent: the profile's patterns
 * tab and its collections tab each have their own segmented control, and one parameter driving both would
 * mean changing the collections segment silently re-pointed the patterns listing.
 *
 * BOTH are sent explicitly, always, rather than leaning on the handler's `public` default — so which
 * listing a request asked for is visible in the URL instead of inferred from a missing parameter, and a
 * later change to a server-side default cannot silently repoint an existing caller. An unrecognised value
 * is refused with 400 rather than answered as `public`, which is why both are typed `ListingBucket` here
 * rather than `string`.
 *
 * `opts.signal` is forwarded INTO `secureFetch`, whose `RequestInit` already accepts `signal` — the
 * token attach, the sub-60-second proactive refresh, and the 401→refresh→retry→alert+redirect are NOT
 * reimplemented as React Query retry (Req 8.3). Spread conditionally, so a caller that omits `opts`
 * passes the same `{ method: 'GET' }` it always did.
 */
export async function fetchSecureProfile(
  screenName: string,
  idToken: string,
  opts?: { signal?: AbortSignal; bucket?: ListingBucket; collectionsBucket?: ListingBucket; limit?: number },
): Promise<SecureProfileResponse> {
  const params = new URLSearchParams({
    bucket: opts?.bucket ?? DEFAULT_LISTING_BUCKET,
    collectionsBucket: opts?.collectionsBucket ?? DEFAULT_LISTING_BUCKET,
  })
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit))
  const url = `${SECURE_API_BASE}/profile/${encodeURIComponent(screenName)}?${params.toString()}`
  console.log('[PATTERNS] fetchSecureProfile:', { url, screenName, hasToken: !!idToken })
  const data = await secureFetch<SecureProfileResponse>(url, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
  console.log('[PATTERNS] fetchSecureProfile data:', {
    profileFound: !!data.profile,
    privatePatternCount: data.profile?.private_pattern_count,
    archivedPatternCount: data.profile?.archived_pattern_count,
    patternCount: data.published_pattern_displays?.length,
  })
  return data
}

// Self-mode secure profile fetch for the logged-in user. Resolves the caller's own
// profile from their identity token (no screen name required), so it can be called at
// login/session init before the screen name is known. Returns the secure response,
// including the optional `permissions` derived ban-state flags for the own-profile case.
//
// `opts.signal` is forwarded INTO `secureFetch`, whose `RequestInit` already accepts `signal` — the
// token attach, the sub-60-second proactive refresh, and the 401→refresh→retry→alert+redirect are NOT
// reimplemented as React Query retry (Req 10.4). The spread is CONDITIONAL, so a caller that omits
// `opts` — `initAuth`, which stays signal-less per Req 4.7 — issues exactly the `{ method: 'GET' }`
// request it issues today, with no `signal: undefined` key added to the `RequestInit` (Req 10.3).
export async function fetchOwnSecureProfile(
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<SecureProfileResponse> {
  const url = `${SECURE_API_BASE}/profile?self=true`
  return secureFetch<SecureProfileResponse>(url, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
}

/*
 * `fetchSecurePatternBucket`, `PatternBucketPage` and `PATTERN_BUCKET_PAGE_SIZE` used to live here.
 * They are GONE (content-visibility-model task 49; BL-330).
 *
 * That function existed to normalise the owner's four bucket segments over the TWO routes that could
 * serve them — `?bucket=` on this envelope for `public`/`unlisted`/`private`, and
 * `GET /api/secure/pattern/archived` for `archived` — and its own doc comment recorded what the first
 * half cost: selecting Unlisted or Private re-requested the WHOLE envelope, discarding the profile
 * record, four social queries, the collections preview and the entire favorites reference-surface walk
 * to fill one tab. It also could not page any of the three non-public segments, because no per-bucket
 * continuation route existed. It named the missing route as a candidate backlog row; this is it.
 *
 * `fetchOwnerPatternBucket` in `api/fetchers/patterns.ts` replaces it against the single route
 * `GET /api/secure/patterns?bucket=&lastKey=`. It lives beside `fetchAuthorPatterns` rather than here,
 * because it no longer reads a profile — and putting the owner's listing next to the anonymous one is
 * what makes the difference between them readable.
 *
 * `?bucket=` on THIS envelope is retained and still sent explicitly as `public`. Page ONE of the owner's
 * Public segment is still served from it, along with the Requirement 12.2 counters and the drafts, and
 * its `patterns_lastKey` is a valid seed for the new route's continuation — the same `LastEvaluatedKey`
 * of the same key condition over the same key range.
 */
